import React, { useMemo, useState, useCallback } from "react";
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import "./endpoints.scss";
import {
  AlertTriangle,
  Monitor,
  User,
  Globe,
  Server,
  Maximize2,
  X,
  Network,
  Filter,
} from "lucide-react";

// Custom Alert Node with handles
const AlertNode = ({ data }) => {
  const severity = (data.severity || "medium").toLowerCase();

  return (
    <div className={`flow-node-alert flow-node-alert--${severity}`}>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "rgba(56, 189, 248, 0.6)", border: "none", width: 10, height: 10 }}
      />
      <div className="flow-node-alert__header">
        <AlertTriangle size={14} />
        <span className={`flow-node-alert__severity flow-node-alert__severity--${severity}`}>
          {data.severity || "Medium"}
        </span>
        <span className="flow-node-alert__entity-count">
          {data.entityCount} entities
        </span>
      </div>
      <div className="flow-node-alert__name" title={data.label}>
        {data.label}
      </div>
      {data.time && (
        <div className="flow-node-alert__time">
          {new Date(data.time).toLocaleString()}
        </div>
      )}
    </div>
  );
};

// Custom Host Node
const HostNode = ({ data }) => {
  return (
    <div className="flow-node-host" title={`Connected to ${data.alertCount} alert(s)`}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "rgba(59, 130, 246, 0.8)", border: "none", width: 8, height: 8 }}
      />
      <Monitor size={20} className="flow-node-entity__icon" />
      <div className="flow-node-entity__label" title={data.label}>
        {data.label}
      </div>
      <div className="flow-node-entity__type">Host</div>
      {data.alertCount > 1 && (
        <div className="flow-node-entity__badge">{data.alertCount}</div>
      )}
    </div>
  );
};

// Custom Account Node
const AccountNode = ({ data }) => {
  return (
    <div className="flow-node-account" title={`Connected to ${data.alertCount} alert(s)`}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "rgba(139, 92, 246, 0.8)", border: "none", width: 8, height: 8 }}
      />
      <User size={20} className="flow-node-entity__icon" />
      <div className="flow-node-entity__label" title={data.label}>
        {data.label}
      </div>
      <div className="flow-node-entity__type">Account</div>
      {data.alertCount > 1 && (
        <div className="flow-node-entity__badge">{data.alertCount}</div>
      )}
    </div>
  );
};

// Custom IP Node (Diamond shape)
const IpNode = ({ data }) => {
  return (
    <div className="flow-node-ip" title={`Connected to ${data.alertCount} alert(s)`}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: "rgba(245, 158, 11, 0.8)",
          border: "none",
          width: 8,
          height: 8,
          top: -4,
          transform: "rotate(-45deg)",
        }}
      />
      <div className="flow-node-ip__content">
        <Globe size={18} className="flow-node-ip__icon" />
        <div className="flow-node-ip__label" title={data.label}>
          {data.label}
        </div>
        <div className="flow-node-ip__type">IP</div>
      </div>
      {data.alertCount > 1 && (
        <div className="flow-node-ip__badge">{data.alertCount}</div>
      )}
    </div>
  );
};

// Custom Resource Node
const ResourceNode = ({ data }) => {
  return (
    <div className="flow-node-resource" title={`Connected to ${data.alertCount} alert(s)`}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "rgba(6, 182, 212, 0.8)", border: "none", width: 8, height: 8 }}
      />
      <Server size={18} className="flow-node-entity__icon" />
      <div className="flow-node-entity__label" title={data.label}>
        {data.label}
      </div>
      <div className="flow-node-entity__type">Resource</div>
      {data.alertCount > 1 && (
        <div className="flow-node-entity__badge">{data.alertCount}</div>
      )}
    </div>
  );
};

const nodeTypes = {
  alert: AlertNode,
  host: HostNode,
  account: AccountNode,
  ip: IpNode,
  resource: ResourceNode,
};

// Parse and deduplicate entities from alerts
const parseAndDeduplicateEntities = (alerts = []) => {
  const alertsData = [];
  const entityMap = new Map(); // key: "type-label", value: { entity data, alertIds: [] }

  alerts.forEach((alert, alertIndex) => {
    const alertId = `alert-${alertIndex}`;
    const raw = alert.entities || alert.Entities;
    let entityList = raw;
    if (typeof raw === "string") {
      try {
        entityList = JSON.parse(raw);
      } catch {
        entityList = [];
      }
    }
    if (!Array.isArray(entityList)) entityList = [];

    let entityCount = 0;

    entityList.forEach((entity) => {
      const type = (entity.Type || entity.type || "").toString().toLowerCase();
      let nodeType = "host";
      let label = "Unknown";

      if (type === "ip") {
        nodeType = "ip";
        label = entity.Address || entity.IpAddress || entity.IP || entity.ip || "IP";
      } else if (type === "account") {
        nodeType = "account";
        label = entity.Name || entity.name || entity.AccountName || "Account";
      } else if (type === "host") {
        nodeType = "host";
        label = entity.HostName || entity.NetBiosName || entity.Name || "Host";
      } else if (type === "azure-resource" || type.includes("resource")) {
        nodeType = "resource";
        const resourceId = entity.ResourceId || entity.AzureResourceId || entity.id || "";
        const parts = resourceId.split("/");
        label = parts[parts.length - 1] || resourceId || "Resource";
      } else {
        nodeType = "host";
        label = entity.HostName || entity.Name || entity.name || "Entity";
      }

      // Create unique key for deduplication
      const entityKey = `${nodeType}-${label.toLowerCase()}`;
      
      if (entityMap.has(entityKey)) {
        // Entity already exists, just add this alert to its connections
        entityMap.get(entityKey).alertIds.push(alertId);
      } else {
        // New entity
        entityMap.set(entityKey, {
          id: `entity-${entityKey}`,
          type: nodeType,
        label,
          alertIds: [alertId],
        });
      }
      entityCount++;
    });

    alertsData.push({
      alertId,
      alertName: alert.displayName || alert.alertName || alert.name || `Alert ${alertIndex + 1}`,
      alertSeverity: alert.alertSeverity || alert.severity || "Medium",
      alertTime: alert.timeGenerated || alert.startTime || alert.endTime,
      entityCount,
    });
  });

  return {
    alerts: alertsData,
    entities: Array.from(entityMap.values()),
  };
};

// Generate nodes and edges with shared entity connections
const generateFlowData = (alertsData, entities, filters) => {
  const nodes = [];
  const edges = [];

  // Filter entities based on active filters
  const filteredEntities = entities.filter((e) => filters[e.type]);

  // Position alerts in a row at the top
  const alertSpacing = 300;
  const alertY = 50;
  const totalAlertsWidth = (alertsData.length - 1) * alertSpacing;
  const alertStartX = 100;

  alertsData.forEach((alertData, index) => {
    nodes.push({
      id: alertData.alertId,
      type: "alert",
      position: { x: alertStartX + index * alertSpacing, y: alertY },
      data: {
        label: alertData.alertName,
        severity: alertData.alertSeverity,
        time: alertData.alertTime,
        entityCount: alertData.entityCount,
      },
    });
  });

  // Group entities by type for organized positioning
  const entityGroups = {
    host: [],
    account: [],
    ip: [],
    resource: [],
  };

  filteredEntities.forEach((entity) => {
    if (entityGroups[entity.type]) {
      entityGroups[entity.type].push(entity);
    }
  });

  // Position entities in rows by type
  const entityY = 220;
  const entitySpacing = 120;
  const rowGap = 130;
  let currentRow = 0;

  const typeOrder = ["host", "account", "ip", "resource"];

  typeOrder.forEach((type) => {
    const typeEntities = entityGroups[type];
    if (typeEntities.length === 0) return;

    const totalWidth = (typeEntities.length - 1) * entitySpacing;
    const startX = alertStartX + (totalAlertsWidth - totalWidth) / 2 + 50;

    typeEntities.forEach((entity, index) => {
      const x = startX + index * entitySpacing;
      const y = entityY + currentRow * rowGap;

      nodes.push({
        id: entity.id,
        type: entity.type,
        position: { x, y },
        data: {
          label: entity.label,
          alertCount: entity.alertIds.length,
        },
      });

      // Create straight line connections to ALL connected alerts
      entity.alertIds.forEach((alertId) => {
        edges.push({
          id: `edge-${alertId}-${entity.id}`,
          source: alertId,
          target: entity.id,
          type: "default", // Straight lines
          style: {
            stroke: getEntityColor(entity.type),
            strokeWidth: entity.alertIds.length > 1 ? 2.5 : 2,
          },
        });
      });
    });

    currentRow++;
  });

  return { nodes, edges };
};

// Helper to get entity type color
const getEntityColor = (type) => {
  switch (type) {
    case "host":
      return "rgba(59, 130, 246, 0.7)";
    case "account":
      return "rgba(139, 92, 246, 0.7)";
    case "ip":
      return "rgba(245, 158, 11, 0.7)";
    case "resource":
      return "rgba(6, 182, 212, 0.7)";
    default:
      return "rgba(148, 163, 184, 0.5)";
  }
};

// Filter Controls Component - compact mode shows icons only
const FilterControls = ({ filters, onFilterChange, compact = false }) => {
  return (
    <div className={`flow-filters ${compact ? "flow-filters--compact" : ""}`}>
      {!compact && (
        <div className="flow-filters__label">
          <Filter size={14} />
          Filter:
        </div>
      )}
      <button
        className={`flow-filters__btn flow-filters__btn--host ${filters.host ? "active" : ""}`}
        onClick={() => onFilterChange("host")}
        title="Hosts"
      >
        <Monitor size={compact ? 16 : 14} />
        {!compact && <span>Hosts</span>}
      </button>
      <button
        className={`flow-filters__btn flow-filters__btn--account ${filters.account ? "active" : ""}`}
        onClick={() => onFilterChange("account")}
        title="Accounts"
      >
        <User size={compact ? 16 : 14} />
        {!compact && <span>Accounts</span>}
      </button>
      <button
        className={`flow-filters__btn flow-filters__btn--ip ${filters.ip ? "active" : ""}`}
        onClick={() => onFilterChange("ip")}
        title="IP Addresses"
      >
        <Globe size={compact ? 16 : 14} />
        {!compact && <span>IPs</span>}
      </button>
      <button
        className={`flow-filters__btn flow-filters__btn--resource ${filters.resource ? "active" : ""}`}
        onClick={() => onFilterChange("resource")}
        title="Resources"
      >
        <Server size={compact ? 16 : 14} />
        {!compact && <span>Resources</span>}
      </button>
    </div>
  );
};

// Legend Component
const Legend = () => (
  <div className="flow-legend">
    <div className="flow-legend__item">
      <div className="flow-legend__icon flow-legend__icon--alert" />
      <span className="flow-legend__label">Alert</span>
    </div>
    <div className="flow-legend__item">
      <div className="flow-legend__icon flow-legend__icon--host" />
      <span className="flow-legend__label">Host</span>
    </div>
    <div className="flow-legend__item">
      <div className="flow-legend__icon flow-legend__icon--account" />
      <span className="flow-legend__label">Account</span>
    </div>
    <div className="flow-legend__item">
      <div className="flow-legend__icon flow-legend__icon--ip" />
      <span className="flow-legend__label">IP Address</span>
    </div>
    <div className="flow-legend__item">
      <div className="flow-legend__icon flow-legend__icon--resource" />
      <span className="flow-legend__label">Resource</span>
    </div>
    <div className="flow-legend__item">
      <div className="flow-legend__icon flow-legend__icon--shared" />
      <span className="flow-legend__label">Shared (multi-alert)</span>
    </div>
  </div>
);

// Flow Canvas Component
const FlowCanvas = ({ nodes, edges, onNodesChange, onEdgesChange, isFullscreen }) => {
  return (
    <div className={`flow-canvas ${isFullscreen ? "flow-canvas--fullscreen" : ""}`}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.15, minZoom: 0.3, maxZoom: 1.5 }}
        minZoom={0.2}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{
          type: "default",
        }}
      >
        <Background color="rgba(56, 189, 248, 0.05)" gap={30} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};

// Fullscreen Modal
const FullscreenModal = ({ onClose, filters, onFilterChange, children, legend }) => {
  return (
    <div className="flow-modal">
      <div className="flow-modal__header">
        <h2 className="flow-modal__title">
          <Network size={20} />
          Alert Entities Map
        </h2>
        <div className="flow-modal__filters">
          <FilterControls filters={filters} onFilterChange={onFilterChange} />
        </div>
        <button className="flow-modal__close" onClick={onClose}>
          <X size={20} />
        </button>
      </div>
      <div className="flow-modal__body">{children}</div>
      <div className="flow-modal__legend">{legend}</div>
    </div>
  );
};

export default function Endpoints({ incident }) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [filters, setFilters] = useState({
    host: true,
    account: true,
    ip: true,
    resource: true,
  });

  const alerts = incident?.alerts || incident?._raw?.alerts || [];

  const { alertsData, entities } = useMemo(() => {
    if (!alerts.length) {
      return { alertsData: [], entities: [] };
    }
    const { alerts: alertsData, entities } = parseAndDeduplicateEntities(alerts);
    return { alertsData, entities };
  }, [alerts]);

  const { initialNodes, initialEdges } = useMemo(() => {
    if (!alertsData.length) {
      return { initialNodes: [], initialEdges: [] };
    }
    const { nodes, edges } = generateFlowData(alertsData, entities, filters);
    return { initialNodes: nodes, initialEdges: edges };
  }, [alertsData, entities, filters]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes/edges when data or filters change
  React.useEffect(() => {
    if (alertsData.length) {
      const { nodes: newNodes, edges: newEdges } = generateFlowData(alertsData, entities, filters);
      setNodes(newNodes);
      setEdges(newEdges);
    }
  }, [alertsData, entities, filters, setNodes, setEdges]);

  const handleFilterChange = useCallback((type) => {
    setFilters((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  }, []);

  const handleExpand = useCallback(() => {
    setIsFullscreen(true);
  }, []);

  const handleCloseFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  // Handle escape key to close fullscreen
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen]);

  if (!alerts.length) {
  return (
      <div className="endpoints-flow">
        <div className="endpoints-flow__section">
          <div className="endpoints-flow__header">
            <h3 className="endpoints-flow__title">Alert Entities Map</h3>
                </div>
          <div className="flow-empty">
            <Network size={48} className="flow-empty__icon" />
            <p className="flow-empty__text">No alerts found for this incident.</p>
                </div>
                </div>
              </div>
    );
  }

  // Count shared entities
  const sharedCount = entities.filter((e) => e.alertIds.length > 1).length;

  return (
    <div className="endpoints-flow">
      <div className="endpoints-flow__section">
        <div className="endpoints-flow__header">
          <h3 className="endpoints-flow__title">
            Alert Entities Map
            {sharedCount > 0 && (
              <span className="endpoints-flow__shared-badge">
                {sharedCount} shared
              </span>
            )}
          </h3>
          <div className="endpoints-flow__actions">
            <FilterControls filters={filters} onFilterChange={handleFilterChange} compact />
            <button className="endpoints-flow__expand-btn" onClick={handleExpand}>
              <Maximize2 size={16} />
            </button>
          </div>
        </div>
        <FlowCanvas
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          isFullscreen={false}
        />
      </div>

      <div className="endpoints-flow__section endpoints-flow__section--legend">
        <Legend />
      </div>

      {isFullscreen && (
        <FullscreenModal
          onClose={handleCloseFullscreen}
          filters={filters}
          onFilterChange={handleFilterChange}
          legend={<Legend />}
        >
          <FlowCanvas
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            isFullscreen={true}
          />
        </FullscreenModal>
      )}
    </div>
  );
}
